import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Truck, Phone, Mail, MapPin, Package, DollarSign,
  Clock, CheckCircle2, AlertTriangle, TrendingUp, Calendar,
  FileText, CreditCard, Loader2, BarChart3, Percent, Building2
} from 'lucide-react';
import { fmtCurrency } from '../lib/localeUtils';
import { keyed } from '../lib/listKeys';

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-500/15 text-slate-700 dark:text-slate-400', label: 'Draft' },
  ordered: { color: 'bg-blue-500/15 text-blue-700 dark:text-blue-400', label: 'Ordered' },
  received: { color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', label: 'Received' },
  cancelled: { color: 'bg-rose-500/15 text-rose-700 dark:text-rose-400', label: 'Cancelled' },
};

export default function SupplierDetailPage() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [processing, setProcessing] = useState(false);

  const fetchDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/suppliers/${supplierId}/details`);
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load supplier details');
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  }, [supplierId, navigate]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const formatCurrency = (amount) => fmtCurrency(amount || 0);

  const handlePaymentSelection = (poId, checked) => {
    if (checked) {
      setSelectedOrders(prev => [...prev, poId]);
    } else {
      setSelectedOrders(prev => prev.filter(id => id !== poId));
    }
  };

  const handleMarkPaid = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Select at least one order');
      return;
    }
    setProcessing(true);
    try {
      await api.put(`/suppliers/${supplierId}/payment`, selectedOrders);
      toast.success(`Marked ${selectedOrders.length} orders as paid`);
      setPaymentDialog(false);
      setSelectedOrders([]);
      fetchDetails();
    } catch (err) {
      toast.error('Failed to record payment');
    } finally {
      setProcessing(false);
    }
  };

  const unpaidOrders = data?.orders?.filter(o => o.payment_status !== 'paid' && o.status !== 'cancelled') || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) return null;

  const { supplier, orders, metrics } = data;

  return (
    <div className="space-y-6" data-testid="supplier-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/suppliers')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {supplier.name}
              </h1>
              <Badge variant={supplier.status === 'active' ? 'default' : 'secondary'}>
                {supplier.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{supplier.notes}</p>
          </div>
        </div>
        <Button className="gap-2" onClick={() => setPaymentDialog(true)} data-testid="record-payment-btn">
          <CreditCard className="h-4 w-4" /> Record Payment
        </Button>
      </div>

      {/* Supplier Info Card */}
      <Card data-testid="supplier-info-card">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Building2 className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Contact Person</p>
                <p className="text-sm font-medium">{supplier.contact_person || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium truncate">{supplier.email || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="text-sm font-medium">{supplier.phone || '-'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Address</p>
                <p className="text-sm font-medium truncate">{supplier.address || '-'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metrics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card data-testid="metric-total-orders">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <FileText className="h-4 w-4" />
              <span className="text-xs">Total Orders</span>
            </div>
            <p className="text-2xl font-bold">{metrics.total_orders}</p>
          </CardContent>
        </Card>

        <Card data-testid="metric-total-spent">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs">Total Spent</span>
            </div>
            <p className="text-2xl font-bold text-primary">{formatCurrency(metrics.total_spent)}</p>
          </CardContent>
        </Card>

        <Card data-testid="metric-pending-balance">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-xs">Pending Balance</span>
            </div>
            <p className={`text-2xl font-bold ${metrics.pending_balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {formatCurrency(metrics.pending_balance)}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-avg-order">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <TrendingUp className="h-4 w-4" />
              <span className="text-xs">Avg Order Value</span>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(metrics.avg_order_value)}</p>
          </CardContent>
        </Card>

        <Card data-testid="metric-on-time">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Clock className="h-4 w-4" />
              <span className="text-xs">On-Time Rate</span>
            </div>
            <p className="text-2xl font-bold">
              {metrics.on_time_delivery_rate !== null ? `${metrics.on_time_delivery_rate}%` : 'N/A'}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-products">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Package className="h-4 w-4" />
              <span className="text-xs">Products</span>
            </div>
            <p className="text-2xl font-bold">{metrics.products_supplied_count}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Orders and Products */}
      <Tabs defaultValue="orders" className="space-y-4">
        <TabsList>
          <TabsTrigger value="orders" className="gap-2">
            <FileText className="h-4 w-4" /> Order History ({orders.length})
          </TabsTrigger>
          <TabsTrigger value="products" className="gap-2">
            <Package className="h-4 w-4" /> Products Supplied
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2">
            <CreditCard className="h-4 w-4" /> Payment History
          </TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <Card data-testid="orders-history-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Purchase Orders Timeline</CardTitle>
              <CardDescription>All orders placed with this supplier</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <div className="space-y-3">
                  {orders.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p>No orders yet</p>
                    </div>
                  ) : (
                    orders.map((order) => {
                      const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.draft;
                      return (
                        <div
                          key={order.po_id}
                          className="flex items-start gap-3 p-3 rounded-lg border hover:bg-accent/30 transition-colors"
                          data-testid={`order-${order.po_id}`}
                        >
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <FileText className="h-5 w-5 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Link
                                to={`/purchases?po=${order.po_id}`}
                                className="text-sm font-semibold text-primary hover:underline"
                              >
                                {order.po_id}
                              </Link>
                              <Badge className={`${statusCfg.color} text-[10px]`}>
                                {statusCfg.label}
                              </Badge>
                              {order.payment_status === 'paid' && (
                                <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-500/50">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Paid
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {order.items?.length || 0} items • {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-sm font-bold">{formatCurrency(order.total_amount || 0)}</p>
                            {order.expected_delivery && (
                              <p className="text-xs text-muted-foreground">
                                Expected: {new Date(order.expected_delivery).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Products Tab */}
        <TabsContent value="products">
          <Card data-testid="products-supplied-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Products Supplied</CardTitle>
              <CardDescription>Items ordered from this supplier</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                {metrics.products_supplied?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p>No products received yet</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {keyed(metrics.products_supplied).map(({ item: product, key }) => (
                      <div key={key} className="p-3 rounded-lg bg-muted/50 text-sm">
                        <Package className="h-4 w-4 text-primary inline mr-2" />
                        {product}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments">
          <Card data-testid="payments-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold">Payment Summary</CardTitle>
              <CardDescription>Track payments made to this supplier</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                  <p className="text-xs text-emerald-600 font-medium">Total Paid</p>
                  <p className="text-xl font-bold text-emerald-600">{formatCurrency(metrics.paid_amount)}</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-xs text-amber-600 font-medium">Pending Balance</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(metrics.pending_balance)}</p>
                </div>
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                  <p className="text-xs text-primary font-medium">Total Business</p>
                  <p className="text-xl font-bold text-primary">{formatCurrency(metrics.total_spent)}</p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-semibold mb-3">Unpaid Orders ({unpaidOrders.length})</p>
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {unpaidOrders.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500 opacity-50" />
                        <p>All orders are paid!</p>
                      </div>
                    ) : (
                      unpaidOrders.map((order) => (
                        <div key={order.po_id} className="flex items-center justify-between p-2 rounded border">
                          <div>
                            <p className="text-sm font-medium">{order.po_id}</p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <p className="text-sm font-semibold text-amber-600">
                            {formatCurrency(order.total_amount || 0)}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Payment Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Record Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select orders to mark as paid for {supplier.name}
            </p>
            <ScrollArea className="h-[250px] border rounded-lg p-2">
              {unpaidOrders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                  <p>No unpaid orders</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {unpaidOrders.map((order) => (
                    <div
                      key={order.po_id}
                      className="flex items-center gap-3 p-2 rounded hover:bg-accent/50"
                    >
                      <Checkbox
                        id={order.po_id}
                        checked={selectedOrders.includes(order.po_id)}
                        onCheckedChange={(checked) => handlePaymentSelection(order.po_id, checked)}
                      />
                      <label htmlFor={order.po_id} className="flex-1 cursor-pointer">
                        <p className="text-sm font-medium">{order.po_id}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </label>
                      <p className="text-sm font-semibold">{formatCurrency(order.total_amount || 0)}</p>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
            {selectedOrders.length > 0 && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/30">
                <p className="text-sm font-medium">
                  Selected: {selectedOrders.length} orders
                </p>
                <p className="text-lg font-bold text-primary">
                  Total: {formatCurrency(
                    unpaidOrders
                      .filter(o => selectedOrders.includes(o.po_id))
                      .reduce((sum, o) => sum + (o.total_amount || 0), 0)
                  )}
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPaymentDialog(false)}>Cancel</Button>
              <Button
                onClick={handleMarkPaid}
                disabled={selectedOrders.length === 0 || processing}
                className="gap-2"
              >
                {processing && <Loader2 className="h-4 w-4 animate-spin" />}
                Mark as Paid
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
